// =============================================================================
// REPARTITION GLOBALE CHART - Donut chart coûts agrégés
// =============================================================================

import { useState, useRef, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts';
import { Info, Wallet } from 'lucide-react';
import { DetailedCostItem } from '@/hooks/useGlobalDetailedCosts';
import {
  formatPourcentage,
  TOOLTIP_EXPLANATIONS,
} from '@/components/charts/chartConfig';

interface Props {
  data: DetailedCostItem[];
  totalCouts: number;
}

// =============================================================================
// SEGMENT ACTIF (HOVER) - Version simplifiée sans animation lourde
// =============================================================================

const renderActiveShape = (props: any) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill,
  } = props;

  // Segment agrandi simplement, sans filtre glow qui cause des saccades
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius - 2}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function RepartitionGlobaleChart({ data, totalCouts }: Props) {
  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>(undefined);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // L'élément actif pour le graphique (hover OU sélectionné)
  const activeIndex = hoveredIndex ?? selectedIndex;
  const activeItem = activeIndex !== undefined ? data[activeIndex] : null;

  // L'élément sélectionné pour l'info-bulle (uniquement au clic)
  const selectedItem = selectedIndex !== undefined ? data[selectedIndex] : null;

  // Gestion hover stabilisée
  const handleEnter = useCallback((index: number) => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setHoveredIndex(index);
  }, []);

  const handleLeave = useCallback(() => {
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredIndex(undefined);
    }, 100);
  }, []);

  // Gestion clic pour afficher l'info-bulle
  const handleClick = useCallback((index: number) => {
    setSelectedIndex(prev => prev === index ? undefined : index);
  }, []);

  const onPieEnter = (_: any, index: number) => {
    handleEnter(index);
  };

  const onPieLeave = () => {
    handleLeave();
  };

  // Explication selon la catégorie
  const getExplanation = (name: string): string => {
    switch (name) {
      case "Main d'œuvre":
        return TOOLTIP_EXPLANATIONS.mainOeuvre;
      case 'Fournisseurs':
        return 'Factures des fournisseurs de matériaux et équipements.';
      case 'Location de véhicule':
        return 'Location de véhicules pour les déplacements et transports sur chantier.';
      case "Achat d'outillage":
        return 'Achat d\'outils et équipements pour les travaux.';
      case "Location d'outillage":
        return 'Location d\'outils et matériel spécialisé.';
      case 'Consommables':
        return 'Petit matériel et consommables utilisés sur le chantier.';
      case 'Frais de déplacement':
        return 'Déplacements, carburant et frais kilométriques.';
      case "Frais d'hébergement":
        return 'Hébergement des équipes sur chantier.';
      case 'Assurance':
        return 'Assurances chantier et garanties.';
      case 'Permis & autorisations':
        return 'Permis de construire, autorisations administratives.';
      case 'Sous-traitant':
        return TOOLTIP_EXPLANATIONS.sousTraitance;
      case 'Autre':
        return 'Autres frais divers.';
      default:
        return `Frais de type "${name}".`;
    }
  };

  if (!data.length || totalCouts === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
        <Wallet className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-sm">Pas de données de coûts</p>
        <p className="text-xs mt-1 opacity-70">Les coûts apparaîtront après les premières dépenses</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col lg:flex-row items-center justify-center gap-6">
      {/* Donut Chart */}
      <div className="relative w-full max-w-[300px] aspect-square">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart style={{ outline: 'none' }} className="[&_path]:outline-none [&_*]:outline-none">
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="85%"
              paddingAngle={3}
              dataKey="value"
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
              onClick={(_: any, index: number) => handleClick(index)}
              animationDuration={800}
              animationEasing="ease-out"
              isAnimationActive={true}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke="transparent"
                  style={{ cursor: 'pointer', outline: 'none' }}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Centre du donut */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {activeItem ? (
            <>
              <span
                className="text-2xl font-bold"
                style={{ color: activeItem.color }}
              >
                {formatPourcentage(activeItem.pct)}
              </span>
              <span className="text-sm text-muted-foreground mt-1">{activeItem.name}</span>
            </>
          ) : (
            <>
              <span className="text-2xl font-bold text-foreground">
                {Math.round(totalCouts).toLocaleString('fr-FR')} €
              </span>
              <span className="text-sm text-muted-foreground mt-1">Coûts totaux</span>
            </>
          )}
        </div>
      </div>

      {/* Légende et détails */}
      <div className="flex flex-col gap-3 min-w-[220px]">
        {data.map((item, index) => (
          <div
            key={item.name}
            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer
              transition-colors duration-200 ease-out
              ${activeIndex === index
                ? 'bg-card border border-border shadow-md'
                : 'bg-card/30 border border-transparent hover:bg-card/50'
              }
              ${selectedIndex === index ? 'ring-2 ring-primary/50' : ''}`}
            onMouseEnter={() => handleEnter(index)}
            onMouseLeave={handleLeave}
            onClick={() => handleClick(index)}
          >
            <div
              className="w-4 h-4 rounded-md flex-shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground truncate">
                  {item.name}
                </span>
                <span className="text-sm font-bold text-foreground whitespace-nowrap">
                  {Math.round(item.value).toLocaleString('fr-FR')} €
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <span className="text-xs text-muted-foreground">
                  {formatPourcentage(item.pct)} du total
                </span>
              </div>
            </div>
          </div>
        ))}

        {/* Info bulle détail - uniquement au clic */}
        {selectedItem && (
          <div className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
            <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">{selectedItem.name}</span>
              <br />
              {getExplanation(selectedItem.name)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
